import { VIEW_HISTORY_ON } from '@vp/render';
import { live, ui } from './bridge';
import { allLocked, hasGroup, primary, selectedObjects } from './selection';
import { copyFromMenu, pasteFromMenu, pickSvgFiles } from './clipboard';
import { armPainter, copyStyle, hasDefaultStyle, painter, pasteStyle, setDefaultStyle } from './style-painter';
import { template } from './diagram';
import { addBookmark, bookmarks, followLink, goToBookmark } from './bookmarks';
import { components, detach, goToMaster, insertInstance, makeComponent, resetOverrides, selectedComponent } from './components';
import { t } from '../i18n';
import { settings } from '../platform';
import { changelog, showChangelog } from '../changelog';
import { feedback } from '../feedback';
import { isDark } from '../theme';
import { commandsFor, insertModuleTemplate, modulePanels, moduleTemplates, runCommand } from '../modules';
import type { useApp } from '../stores/app';

/**
 * Everything the command palette (Ctrl+K) can do: tools, editing, view, canvas, module commands,
 * templates and panels. Built on each open from the current selection, so only what applies is listed.
 */
export interface PaletteCommand {
  id: string;
  label: string;
  group: 'recent' | 'edit' | 'tools' | 'view' | 'canvas' | 'modules';
  keys?: string;
  icon?: string;
  run: () => void;
}

/** "Duplicate (Ctrl+D)" → label "Duplicate", keys "Ctrl+D". */
export function splitKeys(s: string): { label: string; keys?: string } {
  const m = /^(.*?)\s*\(([^)]*)\)$/.exec(s);
  return m ? { label: m[1], keys: m[2] } : { label: s };
}

export interface PaletteHost {
  app: ReturnType<typeof useApp>;
  exportCanvas: (format: 'png' | 'svg' | 'pdf') => void;
  openNotes: () => void;
}

export function paletteCommands(host: PaletteHost): PaletteCommand[] {
  const ed = live.editor;
  const out: PaletteCommand[] = [];
  const add = (group: PaletteCommand['group'], id: string, text: string, run: () => void, icon?: string, keys?: string): void => {
    const s = splitKeys(text);
    out.push({ id, group, label: s.label, keys: keys ?? s.keys, icon, run });
  };
  const ro = ui.readOnly;
  const has = ui.selection.length > 0;
  const one = selectedObjects.value.length === 1 ? selectedObjects.value[0] : null;

  // editing
  if (!ro) {
    add('edit', 'undo', t('cmd.undo'), () => ed?.undo(), 'undo');
    add('edit', 'redo', t('cmd.redo'), () => ed?.redo(), 'redo');
  }
  if (has) {
    add('edit', 'copy', t('cmd.copy'), () => void copyFromMenu(false, host.app.notify), 'copy');
    if (!ro) {
      add('edit', 'cut', t('cmd.cut'), () => void copyFromMenu(true, host.app.notify));
      add('edit', 'duplicate', t('cmd.duplicate'), () => ed?.duplicateSelection(), 'copy');
      add('edit', 'delete', t('cmd.delete'), () => ed?.deleteSelection(), 'trash', 'Delete');
      if (!hasGroup.value && ui.selection.length > 1) add('edit', 'group', t('cmd.group'), () => ed?.groupSelection(), 'group');
      if (hasGroup.value) add('edit', 'ungroup', t('cmd.ungroup'), () => ed?.ungroupSelection(), 'ungroup');
      add('edit', 'lock', allLocked.value ? t('cmd.unlock') : t('cmd.lock'), () => ed?.toggleLock(), allLocked.value ? 'unlock' : 'lock');
      add('edit', 'front', t('cmd.front'), () => ed?.reorderSelection('front'), 'front', 'Ctrl+Shift+]');
      add('edit', 'forward', t('cmd.forward'), () => ed?.reorderSelection('forward'), undefined, 'Ctrl+]');
      add('edit', 'backward', t('cmd.backward'), () => ed?.reorderSelection('backward'), undefined, 'Ctrl+[');
      add('edit', 'back', t('cmd.back'), () => ed?.reorderSelection('back'), 'back', 'Ctrl+Shift+[');
      if (primary.value) {
        add('edit', 'copy-style', t('palette.copyStyle'), () => copyStyle(), 'brush', 'Ctrl+Alt+C');
        add('edit', 'brush', t('palette.brush'), () => armPainter(), 'brush');
        add('edit', 'default-style', t('palette.defaultStyle'), () => setDefaultStyle(primary.value!, true), 'palette');
        if (hasDefaultStyle(primary.value.type)) add('edit', 'reset-default-style', t('palette.resetDefaultStyle'), () => setDefaultStyle(primary.value!, false), 'palette');
      }
      if (painter.style) add('edit', 'paste-style', t('palette.pasteStyle'), () => pasteStyle(), 'brush', 'Ctrl+Alt+V');
      const ratio = selectedObjects.value.length > 0 && selectedObjects.value.every((o) => o.keepRatio === true);
      add('edit', 'keep-ratio', ratio ? t('palette.freeRatio') : t('palette.keepRatio'), () => ed?.setKeepRatio(!ratio), 'ratio');
      if (ui.selection.length > 1) {
        for (const m of ['alignLeft', 'alignCenterX', 'alignRight', 'alignTop', 'alignCenterY', 'alignBottom'] as const) {
          const mode = ({ alignLeft: 'left', alignCenterX: 'centerX', alignRight: 'right', alignTop: 'top', alignCenterY: 'centerY', alignBottom: 'bottom' } as const)[m];
          add('edit', m, t('cmd.' + m), () => ed?.alignSelection(mode), m);
        }
        if (ui.selection.length > 2) {
          add('edit', 'distributeX', t('cmd.distributeX'), () => ed?.alignSelection('distributeX'), 'distributeX');
          add('edit', 'distributeY', t('cmd.distributeY'), () => ed?.alignSelection('distributeY'), 'distributeY');
        }
      }
      if (one && ed?.caps(one).editText) add('edit', 'edit-text', t('cmd.editText'), () => ed.startTextEdit(one.id), 'text', 'Enter');
      const dirs = ['down', 'right', 'up', 'left'] as const;
      const icon = (d: (typeof dirs)[number]) => 'dir' + d[0].toUpperCase() + d.slice(1);
      if (one && ed?.diagramDirection(one.id) && !one.locked) {
        for (const d of dirs) add('edit', 'flow-direction:' + d, `${t('flow.direction')}: ${t('flow.' + d)}`, () => ed.setDiagramDirection(one.id, d), icon(d));
      } else if (ed?.canArrange()) {
        for (const d of dirs) add('edit', 'arrange:' + d, `${t('flow.arrange')}: ${t('flow.' + d)}`, () => ed.arrangeFlow(d), icon(d));
      }
      const fold = ed?.foldState();
      if (fold?.collapse) add('edit', 'collapse', t('flow.collapse'), () => ed!.setCollapsed(ed!.selection, true), 'minus');
      if (fold?.expand) add('edit', 'expand', t('flow.expand'), () => ed!.setCollapsed(ed!.selection, false), 'plus');
    }
    if (one) add('edit', 'notes', t('notes.open'), () => host.openNotes(), 'comment', 'Ctrl+Alt+M');
    if (one?.link) add('edit', 'follow-link', t('links.follow'), () => void followLink(one.link!), 'external');
    if (one && !ro) add('edit', 'link', one.link ? t('links.edit') : t('links.add'), () => (ui.linkFor = one.id), 'hyperlink');
  }
  const sc = selectedComponent.value;
  if (has && !ro && !sc) add('edit', 'make-component', t('components.make'), () => void makeComponent(), 'component', 'Ctrl+Alt+K');
  if (sc && sc.role === 'instance') {
    add('edit', 'go-master', t('components.goMaster'), () => goToMaster(sc.id), 'component');
    if (!ro) {
      add('edit', 'reset-instance', t('components.reset'), () => resetOverrides(sc.root.id), 'component');
      add('edit', 'detach-instance', t('components.detach'), () => detach(sc.root.id), 'component');
    }
  }
  if (!ro) for (const c of components.value) add('tools', 'component:' + c.id, t('components.insertNamed', { name: c.name }), () => insertInstance(c.id), 'component');
  if (!ro) add('edit', 'paste', t('cmd.paste'), () => void pasteFromMenu(host.app.notify));
  add('edit', 'select-all', t('cmd.selectAll'), () => ed?.selectAll(), undefined, 'Ctrl+A');

  // tools
  if (!ro) {
    for (const tool of ed?.toolList ?? []) {
      if (tool.moduleId) add('tools', 'tool:' + tool.id, tool.label ?? tool.id, () => ed?.setTool(tool.id), 'module');
      else if (tool.id !== 'arrow') add('tools', 'tool:' + tool.id, t('tools.' + tool.id), () => ed?.setTool(tool.id), tool.id);
    }
    add('tools', 'diagram', t('diagram.tool'), () => (ui.diagramEdit = { id: null, source: template('flow') }), 'diagram');
  }

  // view
  add('view', 'find', t('search.title'), () => (ui.searchOpen = true), 'search', 'Ctrl+F');
  add('view', 'chat', ui.chatOpen ? t('feed.close') : t('feed.open'), () => (ui.chatOpen = !ui.chatOpen), 'menu');
  add('view', 'zoom-in', t('cmd.zoomIn'), () => ed?.zoomBy(1.25), 'plus', 'Ctrl+=');
  add('view', 'zoom-out', t('cmd.zoomOut'), () => ed?.zoomBy(0.8), 'minus', 'Ctrl+−');
  add('view', 'zoom-reset', t('cmd.zoomReset'), () => ed?.zoomTo(1), undefined, 'Ctrl+0');
  add('view', 'fit-all', t('cmd.fitAll'), () => ed?.fitAll(), 'fit');
  if (has) add('view', 'fit-selection', t('keys.fitSelection'), () => ed?.fitSelection(), 'fit', 'Shift+2');
  // OFF: previous/next view (`VIEW_HISTORY_ON`)
  if (VIEW_HISTORY_ON && ed?.canViewBack) add('view', 'view-back', t('keys.viewBack'), () => ed.backView(), 'chevronLeft');
  if (VIEW_HISTORY_ON && ed?.canViewForward) add('view', 'view-forward', t('keys.viewForward'), () => ed.forwardView(), 'chevron');
  add('view', 'minimap', settings.minimap ? t('palette.hideMinimap') : t('palette.showMinimap'), () => (settings.minimap = !settings.minimap), 'map');
  add('view', 'tree', settings.treeOpen ? t('palette.hideTree') : t('palette.showTree'), () => (settings.treeOpen = !settings.treeOpen), 'tree');
  add('view', 'guides', settings.editor.smartGuides ? t('palette.guidesOff') : t('palette.guidesOn'), () => (settings.editor = { ...settings.editor, smartGuides: !settings.editor.smartGuides }), 'grid');
  add('view', 'theme', isDark.value ? t('palette.lightTheme') : t('palette.darkTheme'), () => (settings.theme = isDark.value ? 'light' : 'dark'), isDark.value ? 'sun' : 'moon');
  for (const b of bookmarks.value) add('view', 'bookmark:' + b.id, t('palette.bookmark', { name: b.name }), () => goToBookmark(b.id), 'bookmark');
  add('view', 'add-bookmark', t('bookmarks.add'), () => void addBookmark(), 'bookmark');
  add('view', 'bookmarks', t('bookmarks.title'), () => (ui.bookmarksOpen = true), 'bookmark');
  add('view', 'zen', ui.zen ? t('zen.exit') : t('zen.enter'), () => (ui.zen = !ui.zen), undefined, 'Z');
  add('view', 'shortcuts', t('keys.title'), () => (ui.keysOpen = true), 'keyboard', '?');

  // canvas and app
  add('canvas', 'save', t('keys.save'), () => void live.session?.flush(), undefined, 'Ctrl+S');
  add('canvas', 'export-png', t('canvas.exportPng'), () => host.exportCanvas('png'), 'download');
  add('canvas', 'export-svg', t('canvas.exportSvg'), () => host.exportCanvas('svg'), 'download');
  if (!ro) add('canvas', 'import-svg', t('canvas.importSvg'), () => pickSvgFiles(), 'upload');
  add('canvas', 'export-pdf', t('canvas.exportPdf'), () => host.exportCanvas('pdf'), 'download');
  add('canvas', 'new-canvas', t('home.newCanvas'), () => void host.app.createCanvas(), 'plus');
  add('canvas', 'home', t('app.myCanvases'), () => host.app.go('home'), 'home');
  add('canvas', 'modules', t('app.modules'), () => host.app.go('modules'), 'apps');
  add('canvas', 'profile', t('app.profile'), () => host.app.go('profile'), 'user');
  add('canvas', 'settings', t('app.settings'), () => host.app.go('settings'), 'settings');
  if (changelog.entries.length) add('canvas', 'whats-new', t('changelog.menu'), showChangelog, 'info');
  if (feedback.email) add('canvas', 'feedback', t('feedback.menu'), () => (feedback.open = true), 'comment');

  // modules: commands for the selection, templates, panels
  if (!ro) {
    for (const c of commandsFor(selectedObjects.value)) add('modules', `cmd:${c.moduleId}:${c.id}`, c.label, () => void runCommand(c.moduleId, c.id), 'module');
    for (const tp of moduleTemplates()) add('modules', `tpl:${tp.moduleId}:${tp.id}`, t('palette.template', { name: tp.label }), () => insertModuleTemplate(tp.moduleId, tp.id), 'module');
  }
  for (const p of modulePanels()) add('modules', `panel:${p.moduleId}`, t('palette.panel', { name: p.title }), () => (ui.sidebar = { kind: 'panel', moduleId: p.moduleId }), p.icon ?? 'module');
  return out;
}

/** Commands matching a query: every word somewhere in the label, the ones starting with it first. */
export function filterCommands(list: PaletteCommand[], query: string): PaletteCommand[] {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!words.length) return list;
  const scored: { c: PaletteCommand; s: number }[] = [];
  for (const c of list) {
    const l = c.label.toLowerCase();
    if (!words.every((w) => l.includes(w))) continue;
    const s = l.startsWith(words[0]) ? 0 : l.split(/[\s(/—-]+/).some((p) => p.startsWith(words[0])) ? 1 : 2;
    scored.push({ c, s });
  }
  return scored.sort((a, b) => a.s - b.s).map((x) => x.c);
}
